import { Dispatch, ReactNode, SetStateAction } from "react";

import { endPoints } from "../../constant/api_constants/url";
import { API_METHODS } from "../../enums";
import {
  iBookingAirportLoaderTypes,
  iCancelHotelsArgs,
  iGetHotelRoomsArgs,
  iHandleHotelRebookArgs,
  iHotelRebookArgs,
  iHotelSummaryArgs,
} from "../../interface";
import { withPaymentMethod } from "../../utils/payment/paymentMethod";
import { shareViaEmailTypes } from "../../types";
import { fetchApi } from "../../utils/apiFunction";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import {
  getHotelRebookIdempotencyKeyField,
  rememberHotelRebookIdempotencyKey,
} from "../../utils/booking/bookingIdempotencyKey";

export const changeFlight = async (accessJWT: string, requestBody: any) => {
  return await fetchApi(accessJWT, endPoints.flightModify, API_METHODS.POST, requestBody, null);
};

export const cancelFlight = async (accessJWT: string, requestBody: any) => {
  return await fetchApi(accessJWT, endPoints.flightCancel, API_METHODS.POST, requestBody, null);
};

// "NewMyTripsCancel & Modify" — new flight cancel/modify endpoints (toggled via
// src/constant/featureSwitches.ts). Same fetchApi transport as the legacy wrappers
// above, so request/response logging + encryption are handled uniformly.
export const cancelFlightRequest = async (accessJWT: string, requestBody: any) => {
  return await fetchApi(accessJWT, endPoints.flightCancelRequest, API_METHODS.POST, requestBody, null);
};

export const modifyFlightSelectSectors = async (accessJWT: string, requestBody: any) => {
  return await fetchApi(accessJWT, endPoints.flightModifySelectSectors, API_METHODS.POST, requestBody, null);
};

export const getPostHotelsSummaryAPI = async (accessJWT: string, requestBody: iHotelSummaryArgs) => {
  consoleLog(["API REQUEST", endPoints.hotelSummary, requestBody]);
  const response = await fetchApi(accessJWT, endPoints.hotelSummary, API_METHODS.POST, requestBody, null);
  consoleLog(["API RESPONSE", endPoints.hotelSummary, response]);
  return response
};

export const getHotelRoomsAPI = async (
  accessJWT: string,
  requestBody: iGetHotelRoomsArgs,
  controller: AbortController
) => {
  return await fetchApi(accessJWT, endPoints.postBookingHotelRooms, API_METHODS.POST, requestBody, null, controller);
};

interface iPostBookingHotelRefundArgs {
  hotels: string[];
  userId: string;
}

export const getPostBookingHotelRefundAPI = async (accessJWT: string, requestBody: iPostBookingHotelRefundArgs) => {
  return await fetchApi(accessJWT, endPoints.hotelRefundAmount, API_METHODS.POST, requestBody, null);
};

export const hotelRebookAPI = async (accessJWT: string, requestBody: iHotelRebookArgs) => {
  const response = await fetchApi(
    accessJWT,
    endPoints.hotelRebook,
    API_METHODS.POST,
    { ...requestBody, paymentDetails: withPaymentMethod(requestBody.paymentDetails) },
    null
  );
  // app-backend mints one idempotency key per rebook and returns it here;
  // remember it so the rebook CONFIRM (after the Stripe round-trip) sends the
  // SAME value back. Its own slot, separate from a new booking's — see
  // utils/booking/bookingIdempotencyKey.ts.
  rememberHotelRebookIdempotencyKey(response?.data?.idempotencyKey);
  return response;
};

export const handleHotelRebookAPI = async (accessJWT: string, requestBody: iHandleHotelRebookArgs) => {
  // Echoes the key `trips/hotels/rebook` returned, so this confirm and the
  // rebook it belongs to are one attempt to the booking engine (app-backend
  // forwards it as the `Idempotency-Key` header, and in the body — the engine
  // call is a POST here). Omitted when there is none.
  const body = { ...requestBody, ...getHotelRebookIdempotencyKeyField() };
  return await fetchApi(accessJWT, endPoints.hotelConfirmRebook, API_METHODS.POST, body, null);
};

export const cancelHotelsAPI = async (accessJWT: string, requestBody: iCancelHotelsArgs) => {
  return await fetchApi(accessJWT, endPoints.cancelHotels, API_METHODS.POST, requestBody, null);
};

export interface PostBookingAirportProps {
  googleInputAddress: string;
  newAddress: string;
  setGoogleInputAddress: (address: string) => void;
  setSelectedAddress: (address: string) => void;
  selectedAddress: string;
  popoverTrigger: ReactNode;
  isPopoverOpen: boolean;
  setIsPopoverOpen: Dispatch<SetStateAction<boolean>>;
  handleApplyButton: () => void;
  label: string;
  value: string;
  handleAirportChange: (flight: { airportCode: string; airportName: string }) => void;
  clearInputValues: () => void;
  hideSearchInput?: boolean;
  indexNumber: number;
  addressChanged: boolean;
  direction?: "ltr" | "rtl";
  className?: string;
  airportName?: string;
  departureInput?: boolean;
  loader: iBookingAirportLoaderTypes;
  isTyping: boolean;
  setIsFocused?: Dispatch<SetStateAction<boolean>>;
}

export const getBaggageAndFareRules = async (accessJWT: string, requestBody: any) => {
  return await fetchApi(accessJWT, endPoints.baggageAndFareRules, API_METHODS.POST, requestBody, null);
};

export const shareBookingDetailsViaEmail = async (accessJWT: string, requestBody: shareViaEmailTypes) => {
  return await fetchApi(accessJWT, endPoints.shareViaEmail, API_METHODS.POST, requestBody, null);
};

// Activity cancellation APIs
export const getActivityCancelReasonsAPI = async (accessJWT: string) => {
  return await fetchApi(accessJWT, endPoints.activityCancelReasons, API_METHODS.GET, null, null);
};

export const getActivityCancelQuoteAPI = async (
  accessJWT: string,
  requestBody: { itineraryId: string; appItineraryId?: string; activities: string[]; userId?: string }
) => {
  return await fetchApi(accessJWT, endPoints.activityCancelQuote, API_METHODS.POST, requestBody, null);
};

export const cancelActivitiesAPI = async (
  accessJWT: string,
  requestBody: { itineraryId: string; appItineraryId?: string; activities: string[]; reasonCode: string; userId?: string }
) => {
  return await fetchApi(accessJWT, endPoints.activityCancel, API_METHODS.POST, requestBody, null);
};

/** Modify step 1 — the options and start times the activity has on a new date. No payment, no price. */
export const changeActivityAPI = async (
  accessJWT: string,
  requestBody: { userId: string; itineraryId: string; activity: { travelDate: string; activityBookingRef: string }[] }
) => {
  return await fetchApi(accessJWT, endPoints.activityChange, API_METHODS.POST, requestBody, null);
};

interface iActivityRebookArgs {
  userId: string;
  itineraryId: string;
  activity: { travelDate: string; activityBookingRef: string; productOptionCode: string; startTime: string }[];
  paymentDetails: { successUrl: string; cancelUrl: string; currency: string; remarks?: string; paymentMethod?: string };
}

/**
 * Modify step 2 — the change body plus the picked `productOptionCode` / `startTime`;
 * answers the price difference and how to pay it (`totalAmountDue`, `paymentLink`,
 * `activity[].quoteRef`). `paymentDetails.paymentMethod` is stamped as on hotel rebook.
 */
export const rebookActivityAPI = async (accessJWT: string, requestBody: iActivityRebookArgs) => {
  return await fetchApi(
    accessJWT,
    endPoints.activityRebook,
    API_METHODS.POST,
    { ...requestBody, paymentDetails: withPaymentMethod(requestBody.paymentDetails) },
    null
  );
};

/** Modify step 3 — commit. `activity[{activityBookingRef, quoteRef, travelDate, productOptionCode, startTime}]`,
 *  `paymentId` only after a Stripe payment. */
export const rebookActivityConfirmAPI = async (
  accessJWT: string,
  requestBody: any
) => {
  return await fetchApi(accessJWT, endPoints.activityRebookConfirm, API_METHODS.POST, requestBody, null);
};
